import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

import { type Conversation } from "shared/apiClient";
import { cn, getText, type Noticeboard } from "shared/lib";
import {
  AssessmentLights,
  AnalysisReportEvents,
  ConversationPhase,
} from "shared/types";

import { useAssessmentLights, useIsReportLocked } from "shared/hooks";
import {
  CaptureEventType,
  useCanAmendFromReportScreen,
  useCaptureEvent,
  useShouldRenderSideBySide,
} from "@/hooks";

import { Button } from "shared/components";
import { Portal } from "@/components/Portal";
import {
  EditProposalDialogContainer,
  type EditProposalDialogContainerRef,
} from "@/components/analysisLayout/editProposal";
import { ShareDialogContainer } from "@/components/analysisLayout/shareDialog";
import { AnalysisLayout } from "./AnalysisLayout";
import { AnalysisSidebarMenuItems } from "./analysisSidebar/AnalysisSidebar";
import { PermitRequirements } from "./permitRequirements";
import { ApplicationRequirements } from "./applicationRequirements";
import { MissingInformation } from "./missingInformation";
import { LotAndProposal } from "./lotAndProposal";
import { NextSteps } from "./nextSteps";

export interface AnalysisLayoutContainerProps {
  conversation: Conversation | undefined | null;
  isPublic?: boolean;
  messages: Conversation["messages"];
  noticeboardData: Noticeboard | undefined | null;
}

export function AnalysisLayoutContainer({
  conversation,
  isPublic,
  messages,
  noticeboardData,
}: AnalysisLayoutContainerProps) {
  const { captureEvent } = useCaptureEvent();

  const [searchParams] = useSearchParams();
  const selectedMenuItem = searchParams.get("selectedMenuItem") as
    | AnalysisSidebarMenuItems
    | "";

  const shouldRenderSideBySide = useShouldRenderSideBySide();

  const assessmentLights = useAssessmentLights(
    conversation,
    noticeboardData,
    "enquiry",
  );

  const isReportGenerating = [
    ConversationPhase.ready_for_assessment,
    ConversationPhase.conv_routing,
  ].includes(conversation?.phase as any);

  const isReportLocked = useIsReportLocked({
    conversation,
    messages: messages || [],
  });

  const canAmendFromReportScreen = useCanAmendFromReportScreen();

  const editProposalDialogRef = useRef<EditProposalDialogContainerRef>(null);

  useEffect(() => {
    if (!isReportGenerating) {
      captureEvent(CaptureEventType.USER_REPORT_RENDERED);
    }
    // eslint-disable-next-line
  }, [isReportGenerating]);

  useEffect(() => {
    if (!isReportGenerating) {
      const timeoutDescriptor = setTimeout(() => {
        captureEvent(CaptureEventType.USER_REPORT_VISIBLE_FOR_30_SECONDS);
      }, 3000);

      return () => {
        clearTimeout(timeoutDescriptor);
      };
    }
  }, [captureEvent, isReportGenerating]);

  useEffect(() => {
    const handleEditProposalEvent = () => {
      if (editProposalDialogRef.current) {
        editProposalDialogRef.current.openDialog();
      }
    };

    document.addEventListener(
      AnalysisReportEvents.EDIT_PROPOSAL,
      handleEditProposalEvent,
    );

    return () => {
      document.removeEventListener(
        AnalysisReportEvents.EDIT_PROPOSAL,
        handleEditProposalEvent,
      );
    };
  }, []);

  const renderReportInteractions = () => {
    if (isPublic) return;

    return (
      <Portal
        elementId={
          shouldRenderSideBySide
            ? "sidebar-footer-portal"
            : "content-footer-portal"
        }
      >
        <div
          className={cn(
            "flex w-full flex-col gap-3 md:flex-row",
            shouldRenderSideBySide ? "flex-col md:flex-col" : "",
          )}
        >
          <ShareDialogContainer conversation={conversation}>
            {(openDialog) => {
              return (
                <Button
                  className="flex-1"
                  onClick={() => {
                    captureEvent(CaptureEventType.USER_CLICKED_SHARE);
                    openDialog();
                  }}
                >
                  {getText("Share")}
                </Button>
              );
            }}
          </ShareDialogContainer>

        </div>
      </Portal>
    );
  };

  const renderItem = () => {
    switch (selectedMenuItem) {
      case AnalysisSidebarMenuItems.PERMIT_REQUIREMENTS:
        return (
          <PermitRequirements
            conversation={conversation}
            messages={messages}
            noticeboardData={noticeboardData}
          />
        );
      case AnalysisSidebarMenuItems.APPLICATION_REQUIREMENTS:
        return (
          <ApplicationRequirements
            conversation={conversation}
            messages={messages}
            noticeboardData={noticeboardData}
          />
        );
      case AnalysisSidebarMenuItems.MISSING_INFORMATION:
        return (
          <MissingInformation
            conversation={conversation}
            messages={messages}
            noticeboardData={noticeboardData}
          />
        );
      case AnalysisSidebarMenuItems.LOT_AND_PROPOSAL:
        return (
          <LotAndProposal
            conversation={conversation}
            messages={messages}
            noticeboardData={noticeboardData}
          />
        );
      case AnalysisSidebarMenuItems.NEXT_STEPS:
        return (
          <NextSteps
            assessmentLights={assessmentLights as AssessmentLights}
            conversation={conversation}
            messages={messages || []}
            noticeboardData={noticeboardData}
          />
        );
      default:
        return (
          <PermitRequirements
            conversation={conversation}
            messages={messages}
            noticeboardData={noticeboardData}
          />
        );
    }
  };

  return (
    <AnalysisLayout
      conversation={conversation}
      messages={messages}
      noticeboardData={noticeboardData}
    >
      {() => {
        return (
          <>
            {renderItem()}
            {renderReportInteractions()}
          </>
        );
      }}
    </AnalysisLayout>
  );
}
